import { NgComponentOutlet } from "@angular/common";
import { Component, type ComponentRef, computed, inject, input, reflectComponentType, ViewContainerRef } from "@angular/core";
import type { CellRendererItem } from "./cell-renderer-item";
import { CellZoomModalComponent } from "./cell-zoom-modal.component";
import type { Page } from "./grid-data.provider";

// Renders whatever component pCellRenderer points to via NgComponentOutlet (which applies
// value/column the same way template bindings do, whether the target declares them as @Input()s
// or signal input()s), instead of manually resolving/creating a ComponentRef ourselves.
@Component({
  selector: "[pCellRenderer]",
  imports: [NgComponentOutlet],
  template: `<ng-container [ngComponentOutlet]="pCellRenderer().component" [ngComponentOutletInputs]="rendererInputs()" />`,
  host: {
    // Since this is now a @Component (not a bare @Directive), its host element is a real DOM
    // node sitting between .p-table-cell and the rendered renderer. "contents" keeps it out of
    // the flex box model so the renderer's own truncate/wrap CSS still sees .p-table-cell directly.
    style: "display: contents;",
    "(click)": "onClick($event)",
  },
})
export class CellRendererDirective {
  private viewContainerRef = inject(ViewContainerRef);

  readonly pCellRenderer = input.required<CellRendererItem>();
  readonly row = input.required<any>();
  readonly rowIndex = input(-1);
  readonly column = input.required<any>();
  readonly value = input.required<any>(); // The value in the cell
  readonly page = input.required<Page>(); // the page the value is on
  readonly addPopup = input(true);
  readonly truncateTextLength = input(-1); // Truncate the text after this many characters. -1 = don't truncate

  private zoomModalRef: ComponentRef<CellZoomModalComponent> | undefined;

  private readonly displayValue = computed(() => {
    let value = this.value();
    if (this.truncateTextLength() >= 0 && typeof value === "string" && value.length > this.truncateTextLength()) {
      value = value.substr(0, this.truncateTextLength()) + "...";
    }
    return value;
  });

  readonly rendererInputs = computed(() => {
    const declared = new Set(reflectComponentType(this.pCellRenderer().component)?.inputs.map((i) => i.propName));
    const inputs: Record<string, unknown> = { value: this.displayValue(), column: this.column() };
    return Object.fromEntries(Object.entries(inputs).filter(([name]) => declared.has(name)));
  });

  /** Sometimes angular was throwing 'ExpressionChangedAfterItHasBeenCheckedError'
   * The first line to blur the parent's parent is intended to fix this.
   * See https://github.com/swimlane/ngx-datatable/issues/721 for more details
   */
  onClick($event: Event) {
    if (!this.addPopup()) {
      return;
    }
    ($event.target as HTMLElement)?.parentElement?.parentElement?.blur();
    this.zoomModalRef?.destroy();
    this.zoomModalRef = this.viewContainerRef.createComponent(CellZoomModalComponent);
    const modal = this.zoomModalRef.instance;
    modal.cellRenderer = this.pCellRenderer();
    modal.row = this.row();
    modal.rowIndex = this.rowIndex();
    modal.column = this.column();
    modal.value = this.value();
    modal.page = this.page();
    modal.closed.subscribe(() => {
      this.zoomModalRef?.destroy();
      this.zoomModalRef = undefined;
    });
    $event.stopPropagation();
  }
}

/*
Format of parameters:

row: {  // maps column ID to row-column value
  "col0": "1",
  "col1": "1",
  "col2": "Column value 2",
  "col3": "Female",
  "col4": "50-54",
  "col5": "United States of America",
  "col6": "n/a",
  "col7": "n/a",
  "col8": "n/a",
  "__rowNumber__": 1
}

column: {
  canAutoResize: boolean
  cellTemplate: TemplateRef
  draggable: boolean
  headerTemplate: TemplateRef
  name: string -- e.g. "Some Name"
  prop: string -- e.g. "col0"
  resizeable: boolean
  sortable: boolean
  width: number
}


 */
